// Purchase orders of the organization (ADR-0047). Available once Media Interactiva enables them. The PDF
// is validated by the server (content, size, no active content) and stored privately; an order only
// reserves credit when Media Interactiva approves it. Amounts are typed in USD and shown as the server
// returns them.
import { useQueryClient } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";
import { toast } from "sonner";
import { csrf } from "@/api/client";
import { API_ORIGIN } from "@/api/config";
import { problemFrom } from "@/api/errors";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Dialog, DialogContent } from "@/ui/dialog";
import { Field } from "@/ui/field";
import { Input } from "@/ui/input";
import { Callout, EmptyState, PageHeader, Panel, Section, Table, Td, Th } from "@/ui/layout";
import { useRouteParams, useTitle } from "../common/page";
import { PO_STATUS, ruleMessage, usd } from "./labels";
import { commercialKeys, useCompany, usePurchaseOrders } from "./queries";

export function PurchaseOrdersPage() {
  const { orgId } = useRouteParams("orgId");
  useTitle("Órdenes de compra");
  const { data: company } = useCompany(orgId);
  const enabled = company?.terms.purchaseOrdersEnabled === true;
  const { data } = usePurchaseOrders(orgId, Boolean(company?.canManage));
  const [open, setOpen] = useState(false);
  return (
    <div className="grid gap-6">
      <PageHeader
        title="Órdenes de compra"
        description="Cada orden aprobada por Media Interactiva reserva capacidad comercial para autorizar tus campañas."
        actions={
          enabled && company.profile?.status === "ACTIVE" ? (
            <Button
              variant="primary"
              onClick={() => {
                setOpen(true);
              }}
            >
              Registrar orden
            </Button>
          ) : null
        }
      />
      {!enabled ? (
        <Callout title="Órdenes de compra no habilitadas">
          Media Interactiva habilita esta modalidad para cada empresa validada. Completa el perfil de tu empresa en
          Configuración y solicítala a tu ejecutivo.
        </Callout>
      ) : null}
      {company && company.terms.creditLimitMicros > 0 ? (
        <Panel className="grid gap-1 p-4 text-sm sm:grid-cols-3">
          <div>
            <div className="text-xs text-muted">Crédito aprobado</div>
            <div className="text-lg font-semibold">{usd(company.terms.creditLimitMicros)}</div>
          </div>
          <div>
            <div className="text-xs text-muted">Comprometido</div>
            <div className="text-lg font-semibold">{usd(company.credit.committedMicros)}</div>
          </div>
          <div>
            <div className="text-xs text-muted">Disponible</div>
            <div className="text-lg font-semibold">{usd(company.credit.availableMicros)}</div>
          </div>
        </Panel>
      ) : null}
      <Section title="Tus órdenes">
        {data?.items.length ? (
          <div className="overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>Referencia</Th>
                  <Th>N.º de orden</Th>
                  <Th>Campaña</Th>
                  <Th className="text-right">Inversión</Th>
                  <Th className="text-right">Aprobado</Th>
                  <Th>Período</Th>
                  <Th>Estado</Th>
                  <Th>PDF</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((po) => (
                  <tr key={po.id}>
                    <Td className="font-mono text-xs">{po.internalRef}</Td>
                    <Td>{po.poNumber}</Td>
                    <Td>{po.campaignName}</Td>
                    <Td className="text-right tabular-nums">{usd(po.amountMicros)}</Td>
                    <Td className="text-right tabular-nums">
                      {po.approvedAmountMicros ? usd(po.approvedAmountMicros) : "—"}
                    </Td>
                    <Td className="whitespace-nowrap">
                      {po.startsOn} → {po.endsOn}
                    </Td>
                    <Td>
                      <Badge tone={PO_STATUS[po.status]?.tone ?? "neutral"}>
                        {PO_STATUS[po.status]?.label ?? po.status}
                      </Badge>
                      {po.decisionReason ? <div className="mt-1 text-xs text-muted">{po.decisionReason}</div> : null}
                    </Td>
                    <Td>
                      <a
                        className="text-brand underline"
                        href={`${API_ORIGIN ?? ""}/orgs/${orgId}/purchase-orders/${po.id}/document`}
                      >
                        Descargar
                      </a>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <EmptyState
            title="Sin órdenes de compra"
            description="Las órdenes que registres aparecen aquí con su estado."
          />
        )}
      </Section>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title="Registrar orden de compra" description="Adjunta el PDF firmado de la orden.">
          <PurchaseOrderForm
            orgId={orgId}
            onDone={() => {
              setOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function csrfHeader(): Record<string, string> {
  const token = csrf.get();
  return token ? { "X-CSRF-Token": token } : {};
}

function PurchaseOrderForm({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    form.set("idempotencyKey", idempotencyKey);
    setPending(true);
    setErrors({});
    setFormError(undefined);
    try {
      const response = await fetch(`${API_ORIGIN ?? ""}/orgs/${orgId}/purchase-orders`, {
        method: "POST",
        body: form,
        credentials: "include",
        headers: csrfHeader(),
      });
      const body: unknown = await response.json().catch(() => null);
      if (!response.ok) {
        const problem = problemFrom(response.status, body);
        const fields = problem.fieldErrors();
        if (fields.document) fields.document = ruleMessage(problem);
        setErrors(fields);
        if (Object.keys(fields).length === 0 || problem.code === "COMMERCIAL_RULE") setFormError(ruleMessage(problem));
        return;
      }
      await queryClient.invalidateQueries({ queryKey: commercialKeys.purchaseOrders(orgId) });
      toast.success(
        `Registramos la orden ${(body as { internalRef: string }).internalRef}. Media Interactiva la revisará.`
      );
      onDone();
    } catch {
      setFormError("No pudimos conectar con InteractAds. Inténtalo de nuevo.");
    } finally {
      setPending(false);
    }
  };
  return (
    <form className="grid gap-3" onSubmit={(e) => void submit(e)}>
      <Field label="Nombre de campaña" error={errors.campaignName}>
        <Input name="campaignName" maxLength={200} required />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Número de orden de compra" error={errors.poNumber}>
          <Input name="poNumber" maxLength={60} required />
        </Field>
        <Field label="Inversión (USD)" error={errors.amountUsd}>
          <Input name="amountUsd" inputMode="decimal" pattern="\d{1,12}(\.\d{1,2})?" placeholder="1500.00" required />
        </Field>
        <Field label="Fecha de inicio" error={errors.startsOn}>
          <Input name="startsOn" type="date" required />
        </Field>
        <Field label="Fecha de finalización" error={errors.endsOn}>
          <Input name="endsOn" type="date" required />
        </Field>
      </div>
      <Field label="Responsable" error={errors.responsibleName}>
        <Input name="responsibleName" maxLength={120} required />
      </Field>
      <Field label="PDF de la orden" hint="PDF simple, sin contraseña. Máximo 10 MB." error={errors.document}>
        <Input name="document" type="file" accept="application/pdf" required />
      </Field>
      {formError ? (
        <p className="text-sm text-danger" role="alert">
          {formError}
        </p>
      ) : null}
      <Button type="submit" variant="primary" disabled={pending}>
        {pending ? "Enviando…" : "Enviar a revisión"}
      </Button>
    </form>
  );
}
